import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Collapse, Grid, Row, Col } from 'react-bootstrap';
import { scroller } from 'react-scroll';
import { translate } from 'react-i18next';
import { scrollSettings } from '../../util/constants';

import Booking from '../../containers/Booking';
import LoginNotAllowed from '../../containers/LoginNotAllowed';
import ContinueConfirmation from '../../containers/ContinueConfirmation';
import CancelReservation from '../../containers/CancelReservation';
import RootSelection from '../../containers/RootSelection';
import SelectEmployerDepartment from '../../containers/SelectEmployerDepartment';
import AppointmentManagement from '../../containers/AppointmentManagement';
import SelectionHandler from '../../util/SelectionHandler';
import LoginOnlyBySso from '../LoginOnlyBySso';
import BookingPhase from '../BookingPhase';
import InnerHtmlContent from '../InnerHtmlContent';
import AppIcon from '../AppIcon';
import i18n from '../../i18n';

class MainPage extends Component {

  constructor(props) {
    super(props);
    this.scroll = this.scroll.bind(this);
    this.getScrollTarget = this.getScrollTarget.bind(this);
    this.getLogicState = this.getLogicState.bind(this);
    this.scrollTimeout = null;
    this.triggerScroll = this.triggerScroll.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { pathRecentlyLoaded, userRecentlyLoggedIn, managementRecentlySelected, wasHistoryInitiallyLoaded, showLoginBySsoView, fetchPathAfterLogin } = this.getLogicState(prevProps);
    // The organization restricts access to the system to users authenticated only via SSO
    if (showLoginBySsoView) {
      this.props.triggerLoginOnlyBySsoView();
      return;
    }
    // Scrolling through this method happens when three conditions have met.
    if (pathRecentlyLoaded || userRecentlyLoggedIn || managementRecentlySelected || wasHistoryInitiallyLoaded || fetchPathAfterLogin) {
      const scrollTarget = this.getScrollTarget(prevProps);
      this.scroll(scrollTarget);
    }
  }

  getLogicState(prevProps) {
    const { userIdentified, viaSso, pathLoaded, loginFirst, historyInitialLoading, historyInitialLoaded, managementSelected,
      uniquePath, loginOnlyBySso, doneIdentifying, user, selectedDepartment, hasEmployer, showCancelReservationScreen,
      fetchPathAfterLogin, selectedEmployer } = this.props;
    const userIsLoggedInWithoutSso = userIdentified && !viaSso;
    const pathRecentlyLoaded = pathLoaded && !prevProps.pathLoaded;
    const userRecentlyLoggedIn = userIdentified && !prevProps.userIdentified && loginFirst;
    const managementRecentlySelected = managementSelected && !prevProps.managementSelected;
    const wasHistoryInitiallyLoaded = !historyInitialLoading && historyInitialLoaded;
    const wasPathRecentlySelected = pathLoaded !== prevProps.pathLoaded;
    const wasManagementUnselected = !managementSelected && prevProps.managementSelected;
    const cancellationScrollView = managementSelected && !viaSso;
    const scrollToSelectedPath = pathLoaded && wasPathRecentlySelected && !uniquePath;
    const showLoginBySsoView = loginOnlyBySso && doneIdentifying && (userIsLoggedInWithoutSso || !userIdentified);
    const hasDepartments = userIdentified && selectedEmployer > -1 &&
      Reflect.has(user.employers[selectedEmployer], 'departments') && user.employers[selectedEmployer].departments.length > 0;
    const hasDepartmentSelected = selectedDepartment > -1;
    const showSelectDepartmentModal = userIdentified && hasEmployer && hasDepartments && !showCancelReservationScreen && !hasDepartmentSelected;
    // if the condition is true we render info box in ServiceInfoBox component
    const serviceInfoBoxExists = i18n.exists('common:serviceInfo.title') || i18n.exists('common:serviceInfo.content');
    return {
      pathRecentlyLoaded,
      userRecentlyLoggedIn,
      managementRecentlySelected,
      wasHistoryInitiallyLoaded,
      wasPathRecentlySelected,
      cancellationScrollView,
      scrollToSelectedPath,
      showLoginBySsoView,
      showSelectDepartmentModal,
      fetchPathAfterLogin,
      wasManagementUnselected,
      serviceInfoBoxExists
    };
  }

  getScrollTarget(prevProps) {
    if (!prevProps) {
      return 'main-page-booking-start';
    }
    // get the necessary variables from the props
    const { loginFirst, userIdentified, showCancelReservationScreen, selectionHandler, triggerInitialScroll, viaSso } = this.props;
    // Do not attempt any scrolling if the user needs to select a department first
    const { wasHistoryInitiallyLoaded, cancellationScrollView, scrollToSelectedPath, showSelectDepartmentModal, fetchPathAfterLogin, wasManagementUnselected, serviceInfoBoxExists } = this.getLogicState(prevProps);
    if (showSelectDepartmentModal) {
      return null;
    }
    if (wasManagementUnselected) {
      return 'main-page-booking-start';
    }
    if (wasHistoryInitiallyLoaded || showCancelReservationScreen || cancellationScrollView) {
      return 'main-page-management-start';
    }
    // if something was selected do not scroll here
    if (selectionHandler.getSelectedOptionIds()) {
      const selectedOptionIds = selectionHandler.getSelectedOptionIds();
      const selectedLevels = selectedOptionIds.length;
      if (selectedLevels) {
        return null;
      }
    }
    // if a path is selected and the user is not logged in and the path has login first
    // show the login form, if there is no service info box
    if ((fetchPathAfterLogin && !serviceInfoBoxExists) || (loginFirst && scrollToSelectedPath && !userIdentified)) {
      return 'intro-box-login';
    }
    // if the user is authenticated go straight to the booking start
    if (loginFirst && scrollToSelectedPath && userIdentified) {
      return 'main-page-booking-start';
    }
    // if selected option go straight to log in box (MBOOK-372)
    if (loginFirst && !scrollToSelectedPath && !userIdentified) {
      return 'intro-box-login';
    }
    // otherwise if the path has just been selected or changed and it is not a login first
    // scroll to the top of the booking page
    if (!loginFirst && scrollToSelectedPath) {
      return 'main-page-booking-start';
    }
    if (!viaSso && !prevProps.triggerInitialScroll && triggerInitialScroll) {
      return 'main-page-booking-start';
    }
    return null;
  }

  scroll(scrollTarget) {
    const extraSettings = {};
    if (scrollTarget !== null) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => { scroller.scrollTo(scrollTarget, { ...scrollSettings, ...extraSettings }) }, 250);
    }
  }

  triggerScroll(wasPathClicked) {
    const { uniquePath, viaSso, managementSelected, unsetManagementOptionWithSinglePath } = this.props;
    if (wasPathClicked && viaSso && uniquePath && managementSelected) {
      // unset the managementSelected state
      unsetManagementOptionWithSinglePath();
    }
    this.scroll(this.getScrollTarget(null));
  }

  shouldContinueConfirmation() {
    const { requireContinueConfirmation, userConfirmedContinue } = this.props;
    return requireContinueConfirmation && !userConfirmedContinue;
  }

  shouldSelectEmployerDepartment() {
    const { userIdentified, hasEmployer, showCancelReservationScreen, user, selectedDepartment, selectedEmployer, shouldSelectEmployer } = this.props;
    const hasEmployerBeenSelected = selectedEmployer > -1;
    const hasEmployers = user && user.employers && user.employers.length > 0;
    const employer = hasEmployers && selectedEmployer > -1 ? user.employers[selectedEmployer] : { };
    const hasDepartments = 'departments' in employer && employer.departments.length > 0;
    const hasDepartmentSelected = selectedDepartment > -1;
    return shouldSelectEmployer || (hasEmployerBeenSelected && userIdentified && hasEmployer && hasDepartments && !showCancelReservationScreen && !hasDepartmentSelected);
  }

  render() {
    const { rootLoaded, showCancelReservationScreen, pathLoaded, roots, loginFailedNotAllowedByEmployer, loginFailedNotDueToMultipleEmployers,
      messageLocalizationKey, managementSelected, showLoginOnlyBySsoView, uniquePath, userIdentified, hasEmployer, requiresEmployer, configurationLoaded, t } = this.props;
    if (!rootLoaded) {
      return null;
    }
    const preventContinuePathEmployerRequired = pathLoaded && requiresEmployer && userIdentified && !hasEmployer;
    const hideLoadedContent = uniquePath && managementSelected;
    const showSelectDepartmentModal = this.shouldSelectEmployerDepartment();
    const continueConfirmation = this.shouldContinueConfirmation();
    const bookingPhaseFooter = (
      <BookingPhase id="booking-footer-info" title={t('booking:footer_info_title')}>
        <Grid fluid>
          <Row>
            <Col xs={12} sm={12} md={6} lg={6} mdPush={6} lgPush={6}>
              <p>
                <InnerHtmlContent content={t('booking:footer_info_p1')} />
              </p>
              <p>
                <InnerHtmlContent content={t('booking:footer_info_p2')} />
              </p>
            </Col>
            <Col
              xs={12} sm={12} md={6} lg={6} mdPull={6} lgPull={6}
              className="footer-phone-wrapper"
            >
              <AppIcon iconName="PHONE" containerClass="footer-phone-image" />
              <div className="footer-phone-box">
                <InnerHtmlContent content={t('booking:footer_info_tel_no')} elementClass={'footer-phone-number'} />
                <InnerHtmlContent content={t('booking:footer_info_tel_no_info')} elementClass={'footer-phone-info'} />
                <div className="footer-email-info">
                  <InnerHtmlContent content={t('booking:footer_info_email_info')} />
                </div>
              </div>
            </Col>
          </Row>
        </Grid>
      </BookingPhase>
    );
    const body = showLoginOnlyBySsoView ? (
      <LoginOnlyBySso />
    ) : (
      <div id="main-page-start" role="main">
        <RootSelection options={roots} triggerInitialScroll={this.triggerScroll} goTo={this.props.route.goTo} />
        <div id="main-page-booking-start">
          <Collapse in={pathLoaded && !hideLoadedContent && configurationLoaded} unmountOnExit>
            <Grid fluid>
              <Booking />
            </Grid>
          </Collapse>
        </div>
        <div id="main-page-management-start">
          <Collapse in={managementSelected} unmountOnExit>
            <Grid fluid>
              <AppointmentManagement />
            </Grid>
          </Collapse>
        </div>
        {bookingPhaseFooter}
      </div>
    );

    return (
      <div className="main-page">
        <div className={'modals'}>
          <LoginNotAllowed
            show={loginFailedNotAllowedByEmployer || loginFailedNotDueToMultipleEmployers || preventContinuePathEmployerRequired}
            messageLocalizationKey={preventContinuePathEmployerRequired ? 'errorEmployerRequired' : messageLocalizationKey}
          />
          <CancelReservation show={showCancelReservationScreen} />
          <SelectEmployerDepartment show={showSelectDepartmentModal} />
          <ContinueConfirmation show={continueConfirmation} />
        </div>
        { body }
      </div>
    );
  }
}

MainPage.propTypes = {
  rootLoaded: PropTypes.bool,
  pathLoaded: PropTypes.bool,
  managementSelected: PropTypes.bool,
  userIdentified: PropTypes.bool.isRequired,
  doneIdentifying: PropTypes.bool.isRequired,
  loginOnlyBySso: PropTypes.bool.isRequired,
  showLoginOnlyBySsoView: PropTypes.bool.isRequired,
  hasEmployer: PropTypes.bool.isRequired,
  requireContinueConfirmation: PropTypes.bool.isRequired,
  messageLocalizationKey: PropTypes.string.isRequired,
  loginFailedNotAllowedByEmployer: PropTypes.bool.isRequired,
  loginFailedNotDueToMultipleEmployers: PropTypes.bool.isRequired,
  userConfirmedContinue: PropTypes.bool.isRequired,
  showCancelReservationScreen: PropTypes.bool,
  roots: PropTypes.array.isRequired,
  loginFirst: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    employers: PropTypes.array.isRequired
  }).isRequired,
  selectedDepartment: PropTypes.number.isRequired,
  uniquePath: PropTypes.bool.isRequired,
  selectionHandler: PropTypes.object.isRequired,
  triggerInitialScroll: PropTypes.bool.isRequired,
  viaSso: PropTypes.bool.isRequired,
  fetchPathAfterLogin: PropTypes.bool.isRequired,
  historyInitialLoading: PropTypes.bool.isRequired,
  historyInitialLoaded: PropTypes.bool.isRequired,
  triggerLoginOnlyBySsoView: PropTypes.func.isRequired,
  selectedEmployer: PropTypes.number.isRequired,
  shouldSelectEmployer: PropTypes.bool.isRequired,
  unsetManagementOptionWithSinglePath: PropTypes.func.isRequired,
  requiresEmployer: PropTypes.bool.isRequired,
  configurationLoaded: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  route: PropTypes.object.isRequired
};

MainPage.defaultProps = {
  roots: [],
  managementSelected: false,
  rootLoaded: false,
  pathLoaded: false,
  pathSelected: -1,
  uniquePath: false,
  showCancelReservationScreen: false,
  organizationBackgroundImageUrl: '',
  loginFirst: true,
  user: { employers: [] },
  selectedDepartment: -1,
  selectionHandler: new SelectionHandler(),
  triggerInitialScroll: false,
  requiresEmployer: false,
  viaSso: false
};

export default translate(['common', 'booking'], { wait: true })(MainPage);



// WEBPACK FOOTER //
// ./src/components/MainPage/MainPage.jsx